/* In-memory mocked client for the Catalogue Governance demo screen — no real backend exists
 * for this domain yet. Mirrors the tasksClientMocked pattern: page components call this client,
 * never the seed data directly. */

import { ICatalogueGovernanceClient } from './ICatalogueGovernanceClient';
import {
  AvailabilitySubstitutionRow,
  BlockedItemRow,
  DataQualityIssueRow,
  DuplicateProductRow,
  DuplicateSuggestedAction,
  GovernanceIssueRow,
  GovernanceSummaryCounts,
  OffContractRequestRow,
  PriceChangeRow,
} from './catalogueGovernanceTypes';
import {
  availabilitySubstitutionsSeed,
  blockedItemsSeed,
  catalogueGovernanceIssuesSeed,
  dataQualityIssuesSeed,
  duplicateProductsSeed,
  offContractRequestsSeed,
  priceChangesSeed,
} from '../../../../mocks/data/catalogueGovernanceDemoData';

// lazy: single in-memory store, resets on page reload - fine for a demo screen with no
// backend. Upgrade path: replace each method body with a real HTTP call once the Catalogue
// Governance API exists, keeping the ICatalogueGovernanceClient interface unchanged.
const store = {
  issues: [...catalogueGovernanceIssuesSeed],
  priceChanges: [...priceChangesSeed],
  availabilitySubstitutions: [...availabilitySubstitutionsSeed],
  offContractRequests: [...offContractRequestsSeed],
  dataQualityIssues: [...dataQualityIssuesSeed],
  duplicateProducts: [...duplicateProductsSeed],
  blockedItems: [...blockedItemsSeed],
};

export class CatalogueGovernanceClientMocked implements ICatalogueGovernanceClient {
  async getSummaryCounts(): Promise<GovernanceSummaryCounts> {
    return {
      pendingPriceChanges: store.priceChanges.filter((p) => p.approvalStatus === 'Pending').length,
      unavailableProducts: store.availabilitySubstitutions.filter((a) => a.availabilityStatus !== 'LimitedStock').length,
      openOffContractRequests: store.offContractRequests.filter((o) => o.approvalStatus === 'Pending').length,
      dataQualityIssues: store.dataQualityIssues.filter((d) => d.status === 'Open').length,
      duplicateProductCandidates: store.duplicateProducts.filter((d) => !d.resolutionNote).length,
      blockedProducts: store.blockedItems.filter((b) => b.status === 'Blocked').length,
      productsMissingAllergens: store.dataQualityIssues.filter((d) => d.issueType === 'MissingAllergens' && d.status === 'Open').length,
      productsWithUomIssues: store.dataQualityIssues.filter((d) => d.issueType === 'BadUomConversion' && d.status === 'Open').length,
    };
  }

  async getIssues(): Promise<GovernanceIssueRow[]> {
    return store.issues;
  }

  async getPriceChanges(): Promise<PriceChangeRow[]> {
    return store.priceChanges;
  }

  async approvePriceChange(id: string): Promise<void> {
    this.updatePriceChangeStatus(id, 'Approved');
  }

  async rejectPriceChange(id: string): Promise<void> {
    this.updatePriceChangeStatus(id, 'Rejected');
  }

  async requestPriceChangeInfo(id: string): Promise<void> {
    this.updatePriceChangeStatus(id, 'MoreInfoRequested');
  }

  private updatePriceChangeStatus(id: string, status: PriceChangeRow['approvalStatus']): void {
    const row = store.priceChanges.find((p) => p.id === id);
    if (row) row.approvalStatus = status;
  }

  async getAvailabilitySubstitutions(): Promise<AvailabilitySubstitutionRow[]> {
    return store.availabilitySubstitutions;
  }

  async markProductUnavailable(id: string): Promise<void> {
    const row = store.availabilitySubstitutions.find((a) => a.id === id);
    if (row) row.availabilityStatus = 'Unavailable';
  }

  async approveSubstitute(id: string): Promise<void> {
    const row = store.availabilitySubstitutions.find((a) => a.id === id);
    if (row) row.approvalStatus = 'Approved';
  }

  async rejectSubstitute(id: string): Promise<void> {
    const row = store.availabilitySubstitutions.find((a) => a.id === id);
    if (row) row.approvalStatus = 'Rejected';
  }

  async notifySites(_id: string): Promise<void> {
    // No-op: no real backend/notification service to call from the demo.
  }

  async getOffContractRequests(): Promise<OffContractRequestRow[]> {
    return store.offContractRequests;
  }

  async approveOffContractOnce(id: string): Promise<void> {
    this.updateOffContractStatus(id, 'ApprovedOnce');
  }

  async approveOffContractAndAddToCatalogue(id: string): Promise<void> {
    this.updateOffContractStatus(id, 'ApprovedAddedToCatalogue');
  }

  async rejectOffContractRequest(id: string): Promise<void> {
    this.updateOffContractStatus(id, 'Rejected');
  }

  private updateOffContractStatus(id: string, status: OffContractRequestRow['approvalStatus']): void {
    const row = store.offContractRequests.find((o) => o.id === id);
    if (row) row.approvalStatus = status;
  }

  async suggestAlternative(_id: string): Promise<void> {
    // No-op: no real backend to persist a suggested alternative against.
  }

  async createSupplierOnboardingRequest(_id: string): Promise<void> {
    // No-op: hands off to the existing Supplier Onboarding flow in a real implementation.
  }

  async getDataQualityIssues(): Promise<DataQualityIssueRow[]> {
    return store.dataQualityIssues;
  }

  async acceptDataQualitySuggestion(id: string): Promise<void> {
    this.updateDataQualityStatus(id, 'Resolved');
  }

  async ignoreDataQualityWarning(id: string): Promise<void> {
    this.updateDataQualityStatus(id, 'Ignored');
  }

  async markDataQualityResolved(id: string): Promise<void> {
    this.updateDataQualityStatus(id, 'Resolved');
  }

  private updateDataQualityStatus(id: string, status: DataQualityIssueRow['status']): void {
    const row = store.dataQualityIssues.find((d) => d.id === id);
    if (row) row.status = status;
  }

  async getDuplicateProducts(): Promise<DuplicateProductRow[]> {
    return store.duplicateProducts;
  }

  async resolveDuplicate(id: string, action: DuplicateSuggestedAction): Promise<void> {
    const row = store.duplicateProducts.find((d) => d.id === id);
    if (row) {
      row.suggestedAction = action;
      row.resolutionNote = `Resolved as "${action}"`;
    }
  }

  async getBlockedItems(): Promise<BlockedItemRow[]> {
    return store.blockedItems;
  }

  async unblockItem(id: string): Promise<void> {
    const row = store.blockedItems.find((b) => b.id === id);
    if (row) row.status = 'Unblocked';
  }
}
